import { Node } from '@xyflow/react';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import ScheduleBuilder from './ScheduleBuilder';

type JourneyNodeType =
  | 'trigger'
  | 'send_email'
  | 'send_sms'
  | 'send_campaign'
  | 'wait_duration'
  | 'wait_event'
  | 'condition'
  | 'add_points'
  | 'remove_points'
  | 'issue_coupon'
  | 'update_attribute'
  | 'exit';

interface MessageTemplate {
  id: string;
  name: string;
  channel: 'email' | 'sms' | 'push' | 'webhook';
  status: 'draft' | 'active' | 'archived';
}

interface Segment {
  id: string;
  name: string;
  type: 'dynamic' | 'static';
}

interface NodeConfigPanelProps {
  node: Node;
  onChange: (id: string, config: Record<string, unknown>) => void;
  onClose: () => void;
  onDelete?: () => void;
}

// Small reusable field components
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-zinc-600">{label}</label>
      {children}
    </div>
  );
}

function Input({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string | number;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs text-zinc-800 focus:outline-none focus:ring-2 focus:ring-emerald-400"
    />
  );
}

function Select({
  value,
  onChange,
  options,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs text-zinc-800 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export default function NodeConfigPanel({ node, onChange, onClose, onDelete }: NodeConfigPanelProps) {
  const type = node.type as JourneyNodeType;
  const config = (node.data.config as Record<string, unknown>) ?? {};

  // ── Template list (shared cache with TemplatesPage / CampaignsPage) ─────────
  const { data: allTemplates = [], isLoading: templatesLoading } = useQuery<MessageTemplate[]>({
    queryKey: ['templates'],
    queryFn: () => api.get('/api/v1/templates').then((r) => r.data),
  });

  // ── Segment list ────────────────────────────────────────────────────────────
  const { data: allSegments = [], isLoading: segmentsLoading } = useQuery<Segment[]>({
    queryKey: ['segments'],
    queryFn: () => api.get('/api/v1/segments').then((r) => r.data),
  });

  // ── Event type registry ──────────────────────────────────────────────────────
  const { data: eventTypesData } = useQuery<{
    items: {
      eventType: string;
      displayName: string | null;
      isSystem: boolean;
      source: string;
    }[];
  }>({
    queryKey: ['event-types'],
    queryFn: () => api.get('/api/v1/events/types', { params: { limit: 500 } }).then((r) => r.data),
  });

  const segmentOptions = [
    { value: '', label: 'Select a segment…' },
    ...allSegments.map((s) => ({ value: s.id, label: s.name })),
  ];

  const emailTemplateOptions = [
    { value: '', label: 'Select a template…' },
    ...allTemplates
      .filter((t) => t.channel === 'email' && t.status === 'active')
      .map((t) => ({ value: t.id, label: t.name })),
  ];

  const smsTemplateOptions = [
    { value: '', label: 'Select a template…' },
    ...allTemplates
      .filter((t) => t.channel === 'sms' && t.status === 'active')
      .map((t) => ({ value: t.id, label: t.name })),
  ];

  function set(key: string, value: unknown) {
    onChange(node.id, { ...config, [key]: value });
  }

  function setNested(key: string, subKey: string, value: unknown) {
    const existing = (config[key] as Record<string, unknown>) ?? {};
    onChange(node.id, { ...config, [key]: { ...existing, [subKey]: value } });
  }

  const TRIGGER_TYPES = [
    { value: 'event', label: 'Event' },
    { value: 'schedule', label: 'Schedule (cron)' },
    { value: 'segment_entry', label: 'Segment Entry' },
    { value: 'manual', label: 'Manual' },
  ];

  const OPERATOR_OPTIONS = [
    { value: '==', label: 'equals' },
    { value: '!=', label: 'not equals' },
    { value: '>', label: 'greater than' },
    { value: '>=', label: 'greater than or equal' },
    { value: '<', label: 'less than' },
    { value: '<=', label: 'less than or equal' },
    { value: 'contains', label: 'contains' },
  ];

  const DURATION_UNITS = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
  ];

  // All known platform event types + a sentinel for custom free-text entry.
  // Dynamically built from the event type registry; falls back to an empty
  // list while loading. System types come first, then custom (user_defined /
  // auto_discovered) types sorted alphabetically.
  const EVENT_TYPE_OPTIONS: { value: string; label: string }[] = [
    { value: '', label: '— Select event type —' },
    ...(eventTypesData?.items ?? [])
      .filter((e) => e.isSystem)
      .map((e) => ({ value: e.eventType, label: e.displayName ?? e.eventType })),
    ...(eventTypesData?.items ?? [])
      .filter((e) => !e.isSystem && e.source === 'user_defined')
      .map((e) => ({
        value: e.eventType,
        label: `${e.displayName ?? e.eventType} (custom)`,
      })),
    ...(eventTypesData?.items ?? [])
      .filter((e) => !e.isSystem && e.source === 'auto_discovered')
      .map((e) => ({ value: e.eventType, label: `${e.eventType} (auto-discovered)` })),
    // Sentinel for free-text entry
    { value: '__custom__', label: 'Custom event type…' },
  ];

  /**
   * Renders a Select + optional free-text input for event type fields.
   * - Known values → Select shows the matching option.
   * - Unknown / custom values → Select shows "Custom event type…" and a text
   *   field appears below pre-filled with the current value.
   * - Selecting "Custom event type…" → text field appears empty.
   * - Selecting a known option → text field is hidden, value is set directly.
   */
  function EventTypeField({
    value,
    onChangeValue,
    label,
  }: {
    value: string;
    onChangeValue: (v: string) => void;
    label: string;
  }) {
    const knownValues = EVENT_TYPE_OPTIONS.map((o) => o.value).filter(
      (v) => v !== '__custom__' && v !== '',
    );
    const isCustom = value !== '' && !knownValues.includes(value);
    const selectValue = isCustom ? '__custom__' : value;

    return (
      <>
        <Field label={label}>
          <Select
            value={selectValue}
            onChange={(v) => {
              if (v === '__custom__') {
                // Keep the existing custom value (or clear if it was a known one)
                onChangeValue(isCustom ? value : '');
              } else {
                onChangeValue(v);
              }
            }}
            options={EVENT_TYPE_OPTIONS}
          />
        </Field>
        {selectValue === '__custom__' && (
          <Field label="Custom event type">
            <Input value={value} onChange={onChangeValue} placeholder="e.g. purchase_completed" />
          </Field>
        )}
      </>
    );
  }

  /**
   * Renders an optional list of field-filter conditions for event triggers.
   * Each row is { field, op, value } and the result is stored as
   *   filterJson: { [field]: { op, value } }
   * which the backend matchesFilter() utility understands.
   */
  function TriggerFilterEditor({
    value: filterJson,
    onChange: onFilterChange,
  }: {
    value: Record<string, { op: string; value: string }>;
    onChange: (v: Record<string, { op: string; value: string }>) => void;
  }) {
    const rows = Object.entries(filterJson);

    function updateRow(oldField: string, key: 'field' | 'op' | 'value', val: string) {
      const updated: Record<string, { op: string; value: string }> = {};
      for (const [f, cond] of Object.entries(filterJson)) {
        if (f === oldField) {
          const newField = key === 'field' ? val : f;
          updated[newField] = {
            ...cond,
            [key === 'field' ? 'op' : key]: key === 'field' ? cond.op : val,
          };
        } else {
          updated[f] = cond;
        }
      }
      onFilterChange(updated);
    }

    function addRow() {
      onFilterChange({ ...filterJson, '': { op: '==', value: '' } });
    }

    function removeRow(field: string) {
      const updated = { ...filterJson };
      delete updated[field];
      onFilterChange(updated);
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-xs font-medium text-zinc-600">Filter conditions</label>
          <button
            onClick={addRow}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            + Add
          </button>
        </div>
        {rows.length === 0 && (
          <p className="text-xs text-zinc-400 italic">No filters — all events will trigger.</p>
        )}
        {rows.map(([field, cond]) => (
          <div key={field} className="flex flex-col gap-1 border border-zinc-200 rounded p-2">
            <Input
              value={field}
              onChange={(v) => updateRow(field, 'field', v)}
              placeholder="payload field (e.g. streakDays)"
            />
            <Select
              value={cond.op}
              onChange={(v) => updateRow(field, 'op', v)}
              options={OPERATOR_OPTIONS}
            />
            <Input
              value={cond.value}
              onChange={(v) => updateRow(field, 'value', v)}
              placeholder="expected value"
            />
            <button
              onClick={() => removeRow(field)}
              className="text-xs text-red-500 hover:text-red-700 text-right"
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    );
  }

  // Derive display unit + value from seconds
  function durationToDisplay(): { value: number; unit: string } {
    const secs = (config.duration as number) ?? 0;
    if (secs >= 86400 && secs % 86400 === 0) return { value: secs / 86400, unit: 'days' };
    if (secs >= 3600 && secs % 3600 === 0) return { value: secs / 3600, unit: 'hours' };
    if (secs >= 60 && secs % 60 === 0) return { value: secs / 60, unit: 'minutes' };
    return { value: secs, unit: 'minutes' };
  }

  function displayToDuration(value: number, unit: string): number {
    if (unit === 'days') return value * 86400;
    if (unit === 'hours') return value * 3600;
    return value * 60;
  }

  const dur = durationToDisplay();

  const valueObj = (config.value as { amount?: number; unit?: string }) ?? {};

  return (
    <div className="w-64 border-l border-zinc-200 bg-white flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 shrink-0">
        <span className="text-sm font-semibold text-zinc-800 capitalize">
          {type?.replace(/_/g, ' ')}
        </span>
        <button
          onClick={onClose}
          className="rounded p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
        >
          <X size={14} />
        </button>
      </div>

      <div className="flex flex-col gap-4 p-4">
        {/* Node name */}
        <Field label="Label">
          <Input
            value={(node.data.name as string) ?? ''}
            onChange={(v) => onChange(node.id, { ...config, __name: v })}
            placeholder="Optional label"
          />
        </Field>

        {/* ── Trigger ── */}
        {type === 'trigger' && (
          <>
            <Field label="Trigger type">
              <Select
                value={(config.triggerType as string) ?? 'event'}
                onChange={(v) => set('triggerType', v)}
                options={TRIGGER_TYPES}
              />
            </Field>
            {(config.triggerType === 'event' || !config.triggerType) && (
              <EventTypeField
                label="Event type"
                value={(config.eventType as string) ?? ''}
                onChangeValue={(v) => set('eventType', v)}
              />
            )}
            {config.triggerType === 'schedule' && (
              <ScheduleBuilder
                value={(config.scheduleExpression as string) ?? ''}
                onChange={(v) => set('scheduleExpression', v)}
              />
            )}
            {config.triggerType === 'segment_entry' && (
              <Field label="Segment ID">
                <Input
                  value={(config.segmentId as string) ?? ''}
                  onChange={(v) => set('segmentId', v)}
                  placeholder="Segment UUID"
                />
              </Field>
            )}
            {config.triggerType === 'manual' && (
              <p className="text-xs text-zinc-400 italic">
                This automation will only start when triggered manually via the API or from the
                customer detail page.
              </p>
            )}
            {/* ── Trigger filter (event-driven only) ── */}
            {(config.triggerType === 'event' || !config.triggerType) && (
              <TriggerFilterEditor
                value={(config.filterJson as Record<string, { op: string; value: string }>) ?? {}}
                onChange={(v) => set('filterJson', v)}
              />
            )}
          </>
        )}

        {/* ── Send Email ── */}
        {type === 'send_email' && (
          <Field label="Template">
            <Select
              value={(config.templateId as string) ?? ''}
              onChange={(v) => {
                const tpl = allTemplates.find((t) => t.id === v);
                onChange(node.id, { ...config, templateId: v, templateName: tpl?.name ?? '' });
              }}
              options={templatesLoading ? [{ value: '', label: 'Loading…' }] : emailTemplateOptions}
              disabled={templatesLoading}
            />
            {!templatesLoading && emailTemplateOptions.length === 1 && (
              <p className="text-xs text-zinc-400 mt-1">No active email templates found.</p>
            )}
          </Field>
        )}

        {/* ── Send SMS ── */}
        {type === 'send_sms' && (
          <Field label="Template">
            <Select
              value={(config.templateId as string) ?? ''}
              onChange={(v) => {
                const tpl = allTemplates.find((t) => t.id === v);
                onChange(node.id, { ...config, templateId: v, templateName: tpl?.name ?? '' });
              }}
              options={templatesLoading ? [{ value: '', label: 'Loading…' }] : smsTemplateOptions}
              disabled={templatesLoading}
            />
            {!templatesLoading && smsTemplateOptions.length === 1 && (
              <p className="text-xs text-zinc-400 mt-1">No active SMS templates found.</p>
            )}
          </Field>
        )}

        {/* ── Send Campaign ── */}
        {type === 'send_campaign' && (
          <>
            <Field label="Segment">
              <Select
                value={(config.segmentId as string) ?? ''}
                onChange={(v) => {
                  const seg = allSegments.find((s) => s.id === v);
                  onChange(node.id, { ...config, segmentId: v, segmentName: seg?.name ?? '' });
                }}
                options={segmentsLoading ? [{ value: '', label: 'Loading…' }] : segmentOptions}
                disabled={segmentsLoading}
              />
              {!segmentsLoading && segmentOptions.length === 1 && (
                <p className="text-xs text-zinc-400 mt-1">No segments found.</p>
              )}
            </Field>
            <Field label="Email Template">
              <Select
                value={(config.templateId as string) ?? ''}
                onChange={(v) => {
                  const tpl = allTemplates.find((t) => t.id === v);
                  onChange(node.id, { ...config, templateId: v, templateName: tpl?.name ?? '' });
                }}
                options={templatesLoading ? [{ value: '', label: 'Loading…' }] : emailTemplateOptions}
                disabled={templatesLoading}
              />
              {!templatesLoading && emailTemplateOptions.length === 1 && (
                <p className="text-xs text-zinc-400 mt-1">No active email templates found.</p>
              )}
            </Field>
          </>
        )}

        {/* ── Wait Duration ── */}
        {type === 'wait_duration' && (
          <>
            <Field label="Duration">
              <Input
                type="number"
                value={dur.value}
                onChange={(v) => set('duration', displayToDuration(Number(v), dur.unit))}
                placeholder="e.g. 3"
              />
            </Field>
            <Field label="Unit">
              <Select
                value={dur.unit}
                onChange={(unit) => set('duration', displayToDuration(dur.value, unit))}
                options={DURATION_UNITS}
              />
            </Field>
          </>
        )}

        {/* ── Wait Event ── */}
        {type === 'wait_event' && (
          <>
            <EventTypeField
              label="Event type"
              value={(config.eventType as string) ?? ''}
              onChangeValue={(v) => set('eventType', v)}
            />
            <Field label="Timeout (seconds, optional)">
              <Input
                type="number"
                value={(config.timeoutSeconds as number) ?? ''}
                onChange={(v) => set('timeoutSeconds', v ? Number(v) : undefined)}
                placeholder="Leave blank for none"
              />
            </Field>
          </>
        )}

        {/* ── Condition ── */}
        {type === 'condition' && (
          <>
            <Field label="Field">
              <Input
                value={(config.field as string) ?? ''}
                onChange={(v) => set('field', v)}
                placeholder="e.g. loyalty.points"
              />
            </Field>
            <Field label="Operator">
              <Select
                value={(config.operator as string) ?? '=='}
                onChange={(v) => set('operator', v)}
                options={OPERATOR_OPTIONS}
              />
            </Field>
            <Field label="Value">
              <Input
                value={(config.value as string) ?? ''}
                onChange={(v) => set('value', v)}
                placeholder="e.g. 100"
              />
            </Field>
            <p className="text-xs text-zinc-400">
              Right handle = true &nbsp;·&nbsp; Left handle = false
            </p>
          </>
        )}

        {/* ── Add / Remove Points ── */}
        {(type === 'add_points' || type === 'remove_points') && (
          <>
            <Field label="Loyalty program slug">
              <Input
                value={(config.programSlug as string) ?? ''}
                onChange={(v) => set('programSlug', v)}
                placeholder="e.g. default"
              />
            </Field>
            <Field label="Points">
              <Input
                type="number"
                value={(config.points as number) ?? ''}
                onChange={(v) => set('points', Number(v))}
                placeholder="e.g. 100"
              />
            </Field>
          </>
        )}

        {/* ── Issue Coupon ── */}
        {type === 'issue_coupon' && (
          <>
            <Field label="Coupon type">
              <Input
                value={(config.couponType as string) ?? ''}
                onChange={(v) => set('couponType', v)}
                placeholder="e.g. discount"
              />
            </Field>
            <Field label="Amount">
              <Input
                type="number"
                value={valueObj.amount ?? ''}
                onChange={(v) => setNested('value', 'amount', Number(v))}
                placeholder="e.g. 10"
              />
            </Field>
            <Field label="Unit">
              <Select
                value={valueObj.unit ?? 'percent'}
                onChange={(v) => setNested('value', 'unit', v)}
                options={[
                  { value: 'percent', label: 'Percent (%)' },
                  { value: 'fixed', label: 'Fixed amount' },
                  { value: 'free', label: 'Free item' },
                ]}
              />
            </Field>
            <Field label="Expires in (days, optional)">
              <Input
                type="number"
                value={(config.expiresInDays as number) ?? ''}
                onChange={(v) => set('expiresInDays', v ? Number(v) : undefined)}
                placeholder="Leave blank for no expiry"
              />
            </Field>
          </>
        )}

        {/* ── Update Attribute ── */}
        {type === 'update_attribute' && (
          <>
            <Field label="Attribute key">
              <Input
                value={(config.attributeKey as string) ?? ''}
                onChange={(v) => set('attributeKey', v)}
                placeholder="e.g. profile.preferredChannel"
              />
            </Field>
            <Field label="Attribute value">
              <Input
                value={(config.attributeValue as string) ?? ''}
                onChange={(v) => set('attributeValue', v)}
                placeholder="e.g. sms"
              />
            </Field>
          </>
        )}

        {/* ── Exit ── */}
        {type === 'exit' && (
          <>
            <Field label="Exit type">
              <Select
                value={(config.exitType as string) ?? 'success'}
                onChange={(v) => set('exitType', v)}
                options={[
                  { value: 'success', label: 'Success' },
                  { value: 'failure', label: 'Failure' },
                ]}
              />
            </Field>
            {config.exitType === 'failure' && (
              <Field label="Reason (optional)">
                <Input
                  value={(config.reason as string) ?? ''}
                  onChange={(v) => set('reason', v)}
                  placeholder="e.g. Customer unsubscribed"
                />
              </Field>
            )}
          </>
        )}
      </div>

      {/* Delete button — only for non-trigger nodes */}
      {type !== 'trigger' && onDelete && (
        <div className="px-4 py-3 border-t border-zinc-200 shrink-0">
          <button
            onClick={() => onDelete()}
            className="w-full rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 hover:border-red-300 transition-colors"
          >
            Elimina nodo
          </button>
        </div>
      )}
    </div>
  );
}
